// Does the signed-in person have an accountant waiting on them? (PRD 63 §5.2)
//
// Read by the sidebar, to decide whether to draw the Requests row and what
// number to put on it. Goes through the store so the question costs one
// request per sign-in however many callers ask it.
//
// UNKNOWN MEANS DO NOT DRAW, the same rule useFirmMembership follows next door
// and for the same reason: this ADDS a row, where guessing wrong offers a
// client screen to somebody with no accountant. The mirror is what keeps
// "briefly unknown" down to the first visit per browser.

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ClientEngagementState,
  clearClientEngagement,
  ensureClientEngagementLoaded,
  getClientEngagementState,
  readEngagementMirror,
  subscribeClientEngagement,
} from '../services/clientEngagementStore';

export function useClientEngagement(): ClientEngagementState {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = user?.authToken;

  const [state, setState] = useState<ClientEngagementState>(() => {
    // Seed from the mirror in the initialiser, not in an effect: an effect runs
    // after the first paint, which is one frame of a missing row on every page
    // load for every client who has one.
    const current = getClientEngagementState();
    if (current.engaged !== null) return current;
    return { engaged: readEngagementMirror(uuid), outstanding: 0, hasLoaded: false };
  });

  useEffect(() => {
    const unsubscribe = subscribeClientEngagement(setState);
    if (authToken) {
      ensureClientEngagementLoaded(uuid);
      // The store may already hold a settled answer from a previous mount;
      // adopt it rather than waiting for the next change to be published.
      setState(getClientEngagementState());
    } else {
      clearClientEngagement();
    }
    return unsubscribe;
  }, [authToken, uuid]);

  // No session, so no accountant — and nothing to wait for.
  if (!authToken) return { engaged: false, outstanding: 0, hasLoaded: true };

  return state;
}
